import { Component, computed, inject, input, signal } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NotificationService } from "@kno2/common/services/notifications";
import { SessionService } from "@kno2/core/session";
import { IntegrationsService } from "@kno2/data-access/integrations";
import { User, UsersService } from "@kno2/data-access/users";
import { DialogService } from "@kno2/shared/dialog";
import { FeaturesService } from "@kno2/shared/features";
import { LoadingSpinnerComponent } from "@kno2/shared/loading-spinner";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";
import { ProfileAuthorizedApplicationsComponent } from "./profile-authorized-applications.component";
import { ProfileEditComponent } from "./profile-edit.component";
import { ProfileEmailNotificationsComponent } from "./profile-email-notifications.component";
import { ProfileIntegrationsComponent } from "./profile-integrations.component";
import { ProfileUserPreferencesComponent } from "./profile-user-preferences.component";

const PROFILE_TABS = ["editProfile", "emailNotifications", "userPreferences", "authorizedApplications", "integrations"];

@Component({
    selector: "kno2-profile-modal",
    imports: [
        NotificationAlertComponent,
        ProfileEditComponent,
        ProfileEmailNotificationsComponent,
        ProfileUserPreferencesComponent,
        ProfileAuthorizedApplicationsComponent,
        ProfileIntegrationsComponent,
        LoadingSpinnerComponent
    ],
    templateUrl: "./profile-modal.component.html"
})
export class ProfileModalComponent {
    private readonly router = inject(Router);
    private readonly dialogService = inject(DialogService);
    private readonly integrationsService = inject(IntegrationsService);
    private readonly notificationService = inject(NotificationService);
    private readonly usersService = inject(UsersService);
    private readonly sessionService = inject(SessionService);
    private readonly featuresService = inject(FeaturesService);

    private readonly capabilities = this.integrationsService.getCapabilities();

    protected form = signal<FormGroup | undefined>(undefined);
    protected readonly user = signal<User>(null);
    protected readonly hasCacheIntegrationData = computed(() => !!this.capabilities()?.capabilities.some((c) => c.display === "cacheIntegrationData"));
    protected readonly error = signal<string>(null);
    protected readonly mfaRequired = computed(() => this.featuresService.isEnabled("MFARequired"));
    protected readonly isSubmitting = signal(false);

    protected readonly currentTab = signal<string>(this.resolveInitialTab());
    public readonly changingPassword = input(false);

    private resolveInitialTab(): string {
        const tab = this.router.lastSuccessfulNavigation()?.extras.state?.["profileTab"] as string;
        return PROFILE_TABS.includes(tab) ? tab : "editProfile";
    }

    public async ngOnInit(): Promise<void> {
        await this.loadUser();

        const user = this.user();
        const { userPreferences } = user;

        this.form.set(
            new FormGroup(
                {
                    firstName: new FormControl(user.firstName, [Validators.required, Validators.maxLength(160)]),
                    lastName: new FormControl(user.lastName, [Validators.required, Validators.maxLength(160)]),
                    phoneNumber: new FormControl(user.phoneNumber, [Validators.required, Validators.maxLength(20)]),
                    email: new FormControl(user.email, [Validators.required, Validators.email]),
                    twoFactorEnabled: new FormControl({
                        value: this.mfaRequired() || !user.canChangeTwoFactorSetting || user.twoFactorEnabled,
                        disabled: this.mfaRequired() || !user.canChangeTwoFactorSetting
                    }),
                    userPreferences: new FormGroup({
                        afterProcessMessage: new FormControl(userPreferences.afterProcessMessage),
                        emailSystemNotifications: new FormControl({
                            value: userPreferences.emailSystemNotifications,
                            disabled: userPreferences.emailAllNotifications && user.isAdministrator
                        }),
                        emailNewMessages: new FormControl(userPreferences.emailNewMessages),
                        emailUnassignedMessages: new FormControl(userPreferences.emailUnassignedMessages),
                        emailUnprocessedMessages: new FormControl(userPreferences.emailUnprocessedMessages),
                        emailFailedMessagesDigest: new FormControl(userPreferences.emailFailedMessagesDigest)
                    })
                },
                {
                    updateOn: "change"
                }
            )
        );
    }

    public async loadUser(): Promise<void> {
        const { userId } = this.sessionService.getProfile();
        this.user.set(await this.usersService.getUserAsync(userId));
    }

    public cancel(): void {
        this.dialogService.close();
    }

    public async save(): Promise<void> {
        try {
            this.isSubmitting.set(true);

            const user = this.user();
            const phoneChanged = this.form().value.phoneNumber !== user.phoneNumber;

            await this.usersService.updateUser({
                ...user,
                ...(phoneChanged && { phoneNumberConfirmed: false, twoFactorEnabled: this.mfaRequired() || !user.canChangeTwoFactorSetting }),
                ...this.form().value
            });
            this.notificationService.success("Profile has been updated.");
            this.dialogService.close(this.form().value);
        } catch {
            this.error.set("There was an error updating your profile.");
        } finally {
            this.isSubmitting.set(false);
        }
    }
}
